import React, { createContext, useContext, useReducer, useEffect, useMemo } from 'react';

import _ from 'lodash';

import { isBookingEvent, isJobEvent } from '@libs/EventTypes';
import { getMoment, getNow } from '@libs/NwMoment';

import { onGetOrderedEventList } from '@hooks/API/Project/UseProject';

import sendProjectDrawerReducer, { ACTION_TYPES } from './sendProjectDrawerReducer';

// Event types the schedule export can handle: everything else is ignored
const EXPORTABLE_EL_TYPES = ['Event', 'Travel', 'Accommodation', 'FollowUp'];

// Key of the selection payload for a non booking event type
const selectionKey = (elType) => `${elType}s`;

const emptySelection = () => ({
    EventDatesModels: [],
    Travels: [],
    FollowUps: [],
    Accommodations: [],
});

const emptyScope = () => ({
    EventDates: [],
    NonBookingEvents: [],
});

/**
 * Builds the project events list the drawer works on: only the handled types, with
 * the common period fields already in place, so that every filter can rely on them.
 */
const buildProjectEventsList = (currentProject) => {
    const eventsList = _.cloneDeep(onGetOrderedEventList(currentProject, 'booking', true, true)).filter((event) =>
        EXPORTABLE_EL_TYPES.includes(event.elType)
    );

    for (const event of eventsList) {
        switch (event.elType) {
            case 'Travel':
                event.CommonStartDate = event.StartDateTime;
                event.CommonEndDate = event.ArrivalDateTime;
                event.Models.forEach((model) => {
                    model.CommonID = model.TravelID;
                });
                break;
            case 'Accommodation':
                event.CommonStartDate = event.FromDate;
                event.CommonEndDate = event.ToDate;
                event.Models.forEach((model) => {
                    model.CommonID = model.AccommodationID;
                    model.Cancel = false;
                });
                break;
            case 'FollowUp':
                event.CommonStartDate = event.StartDate;
                event.CommonEndDate = event.EndDate;
                event.Models.forEach((model) => {
                    model.CommonID = model.FollowUpID;
                });
                break;
            default:
                // Booking event: chronological dates, without the ones left without models
                event.EventDates = _.sortBy(
                    (event.EventDates || []).filter(
                        (eventDate) => eventDate.EventDatesModels && eventDate.EventDatesModels.length > 0
                    ),
                    'FromDate'
                );
                break;
        }
    }

    return eventsList.filter((event) =>
        event.elType === 'Event' ? event.EventDates.length > 0 : event.Models && event.Models.length > 0
    );
};

export const isPastEventDate = (eventDate) =>
    eventDate.FromDate && getMoment(eventDate.FromDate).isBefore(getNow(), 'day');

export const isPastEvent = (event) => event.CommonEndDate && getMoment(event.CommonEndDate).isBefore(getNow(), 'day');

/**
 * Checks if a project event is the one the drawer has been opened from.
 * Non booking events are grouped in the project list while the event drawer opens on a
 * single instance, so the match also looks at the models of the group.
 */
const isTargetEvent = (event, EventID, EventType) => {
    if (!EventID || !EventType) {
        return false;
    }
    if (isBookingEvent(EventType)) {
        return event.elType === 'Event' && event.ID === EventID;
    }
    if (event.elType.toLowerCase() !== EventType.toLowerCase()) {
        return false;
    }
    return event.ID === EventID || event.Models.some((model) => model.CommonID === EventID);
};

/**
 * Scope of the export: the event dates and the non booking events shown in the list.
 * Without a target event everything is in scope, past dates excluded when required.
 */
const buildScope = (eventsList, { includePast, EventID, EventType }) => {
    const scope = emptyScope();
    const hasTargetEvent = eventsList.some((event) => isTargetEvent(event, EventID, EventType));

    eventsList.forEach((event) => {
        const inScope = hasTargetEvent ? isTargetEvent(event, EventID, EventType) : true;
        if (!inScope) {
            return;
        }
        if (event.elType === 'Event') {
            event.EventDates.forEach((eventDate) => {
                if (hasTargetEvent || includePast || !isPastEventDate(eventDate)) {
                    scope.EventDates.push(eventDate.ID);
                }
            });
        } else if (hasTargetEvent || includePast || !isPastEvent(event)) {
            scope.NonBookingEvents.push(event.uniqID);
        }
    });

    return scope;
};

/**
 * Adds or removes every past date from the scope, leaving the other choices untouched.
 */
const applyPastDatesToScope = (eventsList, scope, includePast) => {
    const updatedScope = {
        EventDates: [...scope.EventDates],
        NonBookingEvents: [...scope.NonBookingEvents],
    };

    eventsList.forEach((event) => {
        if (event.elType === 'Event') {
            event.EventDates.filter(isPastEventDate).forEach((eventDate) => {
                updatedScope.EventDates = updatedScope.EventDates.filter((id) => id !== eventDate.ID);
                if (includePast) {
                    updatedScope.EventDates.push(eventDate.ID);
                }
            });
        } else if (isPastEvent(event)) {
            updatedScope.NonBookingEvents = updatedScope.NonBookingEvents.filter((id) => id !== event.uniqID);
            if (includePast) {
                updatedScope.NonBookingEvents.push(event.uniqID);
            }
        }
    });

    return updatedScope;
};

// Default selection: no cancelled models and, on job events, no options
const isDefaultSelectedModel = (event, eventDateModel) =>
    !eventDateModel.Cancel && (!isJobEvent(event.TypeName) || eventDateModel.OptionNumber === 10);

/**
 * Default selection over the whole project. It is built once: the scope decides what is
 * visible, so hiding something never loses the choices made on it.
 */
const buildDefaultSelection = (eventsList) => {
    const selection = emptySelection();

    eventsList.forEach((event) => {
        if (event.elType === 'Event') {
            event.EventDates.forEach((eventDate) => {
                eventDate.EventDatesModels.forEach((eventDateModel) => {
                    if (isDefaultSelectedModel(event, eventDateModel)) {
                        selection.EventDatesModels.push(eventDateModel.ID);
                    }
                });
            });
        } else {
            event.Models.forEach((model) => {
                if (!model.Cancel) {
                    selection[selectionKey(event.elType)].push(model.CommonID);
                }
            });
        }
    });

    return selection;
};

// An active model is confirmed: not cancelled and, on job events, not an option
const isActiveModel = (event, model) =>
    event.elType === 'Event' ? isDefaultSelectedModel(event, model) : !model.Cancel;

/**
 * Selection updates for a list of models: everything off, then the ones the mode keeps on.
 * Mode is 'all', 'active' or 'none'.
 */
const selectionUpdatesForModels = (key, models, getId, event, mode) => {
    const modelsToSelect =
        mode === 'all' ? models : mode === 'none' ? [] : models.filter((model) => isActiveModel(event, model));

    return [
        { key, ids: models.map(getId), checked: false },
        { key, ids: modelsToSelect.map(getId), checked: true },
    ];
};

/**
 * Selection updates for every model of an event
 */
const selectionUpdatesForEvent = (event, mode) => {
    if (event.elType === 'Event') {
        return event.EventDates.flatMap((eventDate) =>
            selectionUpdatesForModels('EventDatesModels', eventDate.EventDatesModels, (model) => model.ID, event, mode)
        );
    }
    return selectionUpdatesForModels(selectionKey(event.elType), event.Models, (model) => model.CommonID, event, mode);
};

/**
 * Models available in the model filter, sorted by contact id.
 */
const extractAvailableModels = (eventsList) => {
    const models = new Set();

    eventsList.forEach((event) => {
        if (event.elType === 'Event') {
            event.EventDates.forEach((eventDate) => {
                eventDate.EventDatesModels.forEach((eventDateModel) => models.add(eventDateModel.ModelID));
            });
        } else {
            event.Models.forEach((model) => models.add(model.ModelID));
        }
    });

    return Array.from(models);
};

/**
 * The list rendered on the right: what the scope keeps, narrowed by the model filters.
 */
const buildVisibleEventsList = (eventsList, scope, selectedModel, hideCancelledModels) => {
    const isVisibleModel = (model) =>
        (!selectedModel || model.ModelID === selectedModel) && (!hideCancelledModels || !model.Cancel);

    return eventsList.reduce((visibleEvents, event) => {
        if (event.elType === 'Event') {
            const eventDates = event.EventDates.filter((eventDate) => scope.EventDates.includes(eventDate.ID))
                .map((eventDate) => ({
                    ...eventDate,
                    EventDatesModels: eventDate.EventDatesModels.filter(isVisibleModel),
                }))
                .filter((eventDate) => eventDate.EventDatesModels.length > 0);

            if (eventDates.length > 0) {
                visibleEvents.push({ ...event, EventDates: eventDates });
            }
        } else if (scope.NonBookingEvents.includes(event.uniqID)) {
            const models = event.Models.filter(isVisibleModel);
            if (models.length > 0) {
                visibleEvents.push({ ...event, Models: models });
            }
        }
        return visibleEvents;
    }, []);
};

/**
 * What actually goes in the e-mails: only what is visible and switched on.
 */
const buildExportSelection = (visibleEventsList, selectedEvents) => {
    const selection = emptySelection();
    const modelIDs = new Set();
    let workDates = 0;
    let otherEvents = 0;

    if (!selectedEvents) {
        return { selection, summary: { models: 0, workDates: 0, otherEvents: 0 } };
    }

    visibleEventsList.forEach((event) => {
        if (event.elType === 'Event') {
            event.EventDates.forEach((eventDate) => {
                const selectedModels = eventDate.EventDatesModels.filter((eventDateModel) =>
                    selectedEvents.EventDatesModels.includes(eventDateModel.ID)
                );
                selectedModels.forEach((eventDateModel) => {
                    selection.EventDatesModels.push(eventDateModel.ID);
                    modelIDs.add(eventDateModel.ModelID);
                });
                if (selectedModels.length > 0) {
                    workDates += 1;
                }
            });
        } else {
            const key = selectionKey(event.elType);
            const selectedModels = event.Models.filter((model) => selectedEvents[key].includes(model.CommonID));
            selectedModels.forEach((model) => {
                selection[key].push(model.CommonID);
                modelIDs.add(model.ModelID);
            });
            if (selectedModels.length > 0) {
                otherEvents += 1;
            }
        }
    });

    return { selection, summary: { models: modelIDs.size, workDates, otherEvents } };
};

// Initial state
const initialState = {
    // Project data
    currentProject: null,
    ModelID: null,
    EventID: null,
    EventType: null,

    // Email items and sending state
    emailItems: null,
    isSending: false,
    errorSending: null,

    // Filters: what can be sent
    selectedModel: null,
    availableModels: [],
    hideCancelledModels: true,
    hidePastDates: true,
    scope: emptyScope(),

    // Selection: what goes in the e-mails
    selectedEvents: null,

    // Whole project, never narrowed by the filters
    projectEventsList: [],

    // Email form data
    subject: '',
    message: '',
    signature: null,
    signatureID: null,
    acknowledgeEvents: true,

    // UI state
    isFetching: false,
};

// Context
const SendProjectDrawerContext = createContext();

// Provider component
export const SendProjectDrawerProvider = ({ children }) => {
    const [state, dispatch] = useReducer(sendProjectDrawerReducer, initialState);
    const { projectEventsList, scope, selectedModel, hideCancelledModels, selectedEvents } = state;

    /**
     * Builds the events list, the scope and the default selection when the project changes.
     * The filters never rebuild it: they only narrow what is shown.
     */
    useEffect(() => {
        if (!state.currentProject) {
            return;
        }

        const eventsList = buildProjectEventsList(state.currentProject);
        const openedFromEvent = eventsList.some((event) => isTargetEvent(event, state.EventID, state.EventType));
        const initialScope = buildScope(eventsList, {
            includePast: false,
            EventID: state.EventID,
            EventType: state.EventType,
        });

        // Opening from a past event has to show it, so the shortcut starts off
        const hasPastInScope = eventsList.some((event) => {
            if (event.elType === 'Event') {
                return event.EventDates.some(
                    (eventDate) => isPastEventDate(eventDate) && initialScope.EventDates.includes(eventDate.ID)
                );
            }
            return isPastEvent(event) && initialScope.NonBookingEvents.includes(event.uniqID);
        });

        dispatch({
            type: ACTION_TYPES.INITIALIZE_EVENTS,
            payload: {
                projectEventsList: eventsList,
                availableModels: extractAvailableModels(eventsList),
                scope: initialScope,
                selectedEvents: buildDefaultSelection(eventsList),
                hidePastDates: openedFromEvent ? !hasPastInScope : true,
            },
        });
    }, [state.currentProject, state.EventID, state.EventType]);

    const visibleEventsList = useMemo(
        () => buildVisibleEventsList(projectEventsList, scope, selectedModel, hideCancelledModels),
        [projectEventsList, scope, selectedModel, hideCancelledModels]
    );

    const { selection: exportSelection, summary: selectionSummary } = useMemo(
        () => buildExportSelection(visibleEventsList, selectedEvents),
        [visibleEventsList, selectedEvents]
    );

    /**
     * Switches a list of models on or off in the selection.
     */
    const updateSelection = (updates) => {
        const updatedSelection = { ...(selectedEvents || emptySelection()) };

        updates.forEach(({ key, ids, checked }) => {
            const remaining = updatedSelection[key].filter((id) => !ids.includes(id));
            updatedSelection[key] = checked ? [...remaining, ...ids] : remaining;
        });

        dispatch({ type: ACTION_TYPES.SET_SELECTED_EVENTS, payload: updatedSelection });
    };

    // Action creators
    const actions = {
        setProjectData: (projectData) => {
            dispatch({ type: ACTION_TYPES.SET_PROJECT_DATA, payload: projectData });
        },

        setEmailItems: (emailItems) => {
            dispatch({ type: ACTION_TYPES.SET_EMAIL_ITEMS, payload: emailItems });
        },

        setSendingState: (isSending) => {
            dispatch({ type: ACTION_TYPES.SET_SENDING_STATE, payload: isSending });
        },

        setErrorSending: (error) => {
            dispatch({ type: ACTION_TYPES.SET_ERROR_SENDING, payload: error });
        },

        setSelectedModel: (modelID) => {
            dispatch({ type: ACTION_TYPES.SET_SELECTED_MODEL, payload: modelID });
        },

        setHideCancelledModels: (hide) => {
            dispatch({ type: ACTION_TYPES.SET_HIDE_CANCELLED_MODELS, payload: hide });
        },

        setHidePastDates: (hide) => {
            dispatch({
                type: ACTION_TYPES.SET_HIDE_PAST_DATES,
                payload: {
                    hidePastDates: hide,
                    scope: applyPastDatesToScope(projectEventsList, scope, !hide),
                },
            });
        },

        setSelectedEvents: (events) => {
            dispatch({ type: ACTION_TYPES.SET_SELECTED_EVENTS, payload: events });
        },

        setEmailFormData: (formData) => {
            dispatch({ type: ACTION_TYPES.SET_EMAIL_FORM_DATA, payload: formData });
        },

        setUIState: (uiState) => {
            dispatch({ type: ACTION_TYPES.SET_UI_STATE, payload: uiState });
        },

        updateEmailItems: (updates) => {
            dispatch({ type: ACTION_TYPES.UPDATE_EMAIL_ITEMS, payload: updates });
        },

        updateRecipient: (modelID, updates) => {
            dispatch({
                type: ACTION_TYPES.UPDATE_RECIPIENT,
                payload: { modelID, updates },
            });
        },

        updateProjectDocument: (documentID, updates) => {
            dispatch({
                type: ACTION_TYPES.UPDATE_PROJECT_DOCUMENT,
                payload: { documentID, updates },
            });
        },

        updateModelDocument: (modelID, documentID, updates) => {
            dispatch({
                type: ACTION_TYPES.UPDATE_MODEL_DOCUMENT,
                payload: { modelID, documentID, updates },
            });
        },

        /**
         * Scope: shows or hides a single work date of a booking event.
         */
        toggleEventDate: (eventDateID, checked) => {
            const eventDates = scope.EventDates.filter((id) => id !== eventDateID);
            dispatch({
                type: ACTION_TYPES.SET_SCOPE,
                payload: {
                    ...scope,
                    EventDates: checked ? [...eventDates, eventDateID] : eventDates,
                },
            });
        },

        /**
         * Scope: shows or hides a whole event, with all of its work dates.
         */
        toggleEvent: (event, checked) => {
            if (event.elType === 'Event') {
                const eventDateIDs = event.EventDates.map((eventDate) => eventDate.ID);
                const eventDates = scope.EventDates.filter((id) => !eventDateIDs.includes(id));
                dispatch({
                    type: ACTION_TYPES.SET_SCOPE,
                    payload: {
                        ...scope,
                        EventDates: checked ? [...eventDates, ...eventDateIDs] : eventDates,
                    },
                });
                return;
            }

            const nonBookingEvents = scope.NonBookingEvents.filter((id) => id !== event.uniqID);
            dispatch({
                type: ACTION_TYPES.SET_SCOPE,
                payload: {
                    ...scope,
                    NonBookingEvents: checked ? [...nonBookingEvents, event.uniqID] : nonBookingEvents,
                },
            });
        },

        /**
         * Selection: switches a single model on or off.
         */
        selectModel: (key, id, checked) => {
            updateSelection([{ key, ids: [id], checked }]);
        },

        /**
         * Selection: the visible models of a single work date, by mode.
         */
        selectDateModels: (event, eventDate, mode) => {
            updateSelection(
                selectionUpdatesForModels(
                    'EventDatesModels',
                    eventDate.EventDatesModels,
                    (eventDateModel) => eventDateModel.ID,
                    event,
                    mode
                )
            );
        },

        /**
         * Selection: the visible models of a single event, by mode.
         */
        selectEventModels: (event, mode) => {
            updateSelection(selectionUpdatesForEvent(event, mode));
        },

        /**
         * Selection: every model currently visible, cancelled models and options included.
         */
        selectAllVisibleModels: () => {
            updateSelection(visibleEventsList.flatMap((event) => selectionUpdatesForEvent(event, 'all')));
        },

        /**
         * Selection: back to the default, no cancelled models and no options.
         */
        selectDefaultModels: () => {
            dispatch({
                type: ACTION_TYPES.SET_SELECTED_EVENTS,
                payload: buildDefaultSelection(projectEventsList),
            });
        },
    };

    const value = {
        state,
        actions,
        visibleEventsList,
        exportSelection,
        selectionSummary,
    };

    return <SendProjectDrawerContext.Provider value={value}>{children}</SendProjectDrawerContext.Provider>;
};

// Custom hook to use the context
export const useSendProjectDrawer = () => {
    const context = useContext(SendProjectDrawerContext);
    if (!context) {
        throw new Error('useSendProjectDrawer must be used within a SendProjectDrawerProvider');
    }
    return context;
};

export default SendProjectDrawerContext;
